import { apiSlice } from '../apiSlice';
import { Paginated, SuccessResponse } from '../DTO/common.interface';
import {
	AdmissionRequestDetailDTO,
	admissionRequestFilter,
	AdmissionRequestPageDTO,
	ChangeHistoryDTO,
	ChangeHistoryFilter,
	CreateFamilyCarerRequest,
	EditContactRequest,
	FamilyCarersDTO,
	EditHealthRequest,
	EditPersonalRequest,
	EditReferrerRequest,
	EditWorkRequest,
	FamilyMemberRelationshipDTO,
	MemberInfoFilter,
	MemberInfoPageDTO,
} from '../DTO/member-management/admissionRequest';

export const admissionRequest = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getAdmissionRequestAll: builder.query<
			SuccessResponse<Paginated<AdmissionRequestPageDTO>>,
			admissionRequestFilter
		>({
			query: (body) => ({
				url: `/web/admission-request/page`,
				method: 'POST',
				body,
			}),
		}),
		getAdmissionRequestNot: builder.query<
			SuccessResponse<Paginated<AdmissionRequestPageDTO>>,
			admissionRequestFilter
		>({
			query: (body) => ({
				url: `/web/admission-request/page/not-counted`,
				method: 'POST',
				body,
			}),
		}),
		getAdmissionRequestDetail: builder.query<
			SuccessResponse<AdmissionRequestDetailDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/admission-request/${id}`,
				method: 'GET',
			}),
			providesTags: ['AdmissionRequestDetail'],
		}),

		editPersonal: builder.mutation<SuccessResponse<any>, EditPersonalRequest>({
			query: (body) => ({
				url: `/web/admission-request/${body.id}/personal`,
				method: 'PUT',
				body,
			}),
		}),
		editContact: builder.mutation<SuccessResponse<any>, EditContactRequest>({
			query: (body) => ({
				url: `/web/admission-request/${body.id}/contact`,
				method: 'PUT',
				body,
			}),
		}),
		editFamily: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/admission-request/${body.id}/family-cares`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['FamilyCarersList', 'AdmissionRequestDetail'],
		}),
		// Appends a single carer and returns it with the id the server assigned,
		// unlike the PUT above which replaces the member's whole list.
		createFamilyCarer: builder.mutation<
			SuccessResponse<FamilyCarersDTO>,
			CreateFamilyCarerRequest
		>({
			query: ({ userId, ...body }) => ({
				url: `/web/admission-request/${userId}/family-cares`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['FamilyCarersList', 'AdmissionRequestDetail'],
		}),
		editWork: builder.mutation<SuccessResponse<any>, EditWorkRequest>({
			query: (body) => ({
				url: `/web/admission-request/${body.id}/education`,
				method: 'PUT',
				body,
			}),
		}),
		editMembership: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/admission-request/${body.id}/membership`,
				method: 'PUT',
				body,
			}),
		}),
		editCase: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/admission-request/${body.id}/case-status`,
				method: 'PUT',
				body,
			}),
		}),
		editReferrer: builder.mutation<SuccessResponse<any>, EditReferrerRequest>({
			query: (body) => ({
				url: `/web/admission-request/${body.id}/referrer`,
				method: 'PUT',
				body,
			}),
		}),
		editShare: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/admission-request/${body.id}/other-organisation`,
				method: 'PUT',
				body,
			}),
		}),
		editHealth: builder.mutation<SuccessResponse<any>, EditHealthRequest>({
			query: (body) => ({
				url: `/web/admission-request/${body.id}/mental-health`,
				method: 'PUT',
				body,
			}),
		}),
		deleteAdmissionRequest: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/admission-request/${id}`,
				method: 'DELETE',
			}),
		}),
		getPendingSubmissionList: builder.query<
			SuccessResponse<Paginated<AdmissionRequestPageDTO>>,
			admissionRequestFilter
		>({
			query: (body) => ({
				url: `/web/admission-request/page/pending-submission/${body.my}`,
				method: 'POST',
				body,
			}),
		}),
		getAwaitingList: builder.query<
			SuccessResponse<Paginated<AdmissionRequestPageDTO>>,
			admissionRequestFilter
		>({
			query: (body) => ({
				url: `/web/admission-request/page/awaiting-assignment/${body.my}`,
				method: 'POST',
				body,
			}),
		}),
		getPendingList: builder.query<
			SuccessResponse<Paginated<AdmissionRequestPageDTO>>,
			admissionRequestFilter
		>({
			query: (body) => ({
				url: `/web/admission-request/page/pending-intake/${body.my}`,
				method: 'POST',
				body,
			}),
		}),
		getChangeHistoryList: builder.query<
			SuccessResponse<Paginated<ChangeHistoryDTO>>,
			ChangeHistoryFilter
		>({
			query: (body) => ({
				url: `/web/change-history/${body.id}/page`,
				method: 'POST',
				body,
			}),
		}),
		getChangeHistoryListDetail: builder.mutation<
			SuccessResponse<ChangeHistoryDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/change-history/list/${id}`,
				method: 'GET',
			}),
		}),
		getMemberList: builder.query<
			SuccessResponse<Paginated<MemberInfoPageDTO>>,
			MemberInfoFilter
		>({
			query: (body) => ({
				url: `/web/member/page/${body.type}`,
				method: 'POST',
				body,
			}),
			providesTags: ['MemberFamilyList'],
		}),
		getNextFileNo: builder.query<SuccessResponse<any>, null>({
			query: () => ({
				url: `/web/membership/next-file-no`,
				method: 'POST',
			}),
		}),
		registerProgram: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/membership/${body.id}/register-program`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		registerCase: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/membership/${body.id}/register-case`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		openCase: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/membership/${body.id}/open-case`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		closeCase: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/membership/${body.id}/close-case`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail', 'FamilyFormList'],
		}),
		closeAllProgram: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/membership/${body.id}/close-all-program`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail', 'FamilyFormList'],
		}),
		closeAllCase: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/membership/${body.id}/close-all-case`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail', 'FamilyFormList'],
		}),
		getFamilyMemberGroupList: builder.mutation<
			SuccessResponse<FamilyMemberRelationshipDTO[]>,
			any
		>({
			query: (id) => ({
				url: `/web/family-member/${id}/family-member-group`,
				method: 'GET',
			}),
		}),
		submitToIC: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/submit-to-ic`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		returnFromIC: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/return-from-ic`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		assignCaseWorkers: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/assign-case-workers`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		assignProgramWorkers: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/assign-program-workers`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		closeNotSuitable: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/close`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail', 'FamilyFormList'],
		}),
		transferCaseWorkers: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/transfer-case-workers`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		updateShareProfessionals: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/update-shared-professionals`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		transferProgramWorkers: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/transfer-program-workers`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		clearAllCaseWorkers: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/clear-all-case-workers`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		clearAllProgramWorkers: builder.mutation<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/other-action/${body.id}/clear-all-program-workers`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['AdmissionRequestDetail'],
		}),
		getNextFileNoString: builder.mutation<
			SuccessResponse<any>,
			{ fiscalYear: string }
		>({
			query: (body) => {
				return {
					url: `/web/membership/next-file-no`,
					method: 'POST',
					body,
				};
			},
		}),
	}),
});

export const {
	useGetAdmissionRequestAllQuery,
	useGetAdmissionRequestNotQuery,
	useGetAdmissionRequestDetailQuery,
	useEditPersonalMutation,
	useEditContactMutation,
	useEditFamilyMutation,
	useCreateFamilyCarerMutation,
	useEditWorkMutation,
	useEditMembershipMutation,
	useEditCaseMutation,
	useEditReferrerMutation,
	useEditShareMutation,
	useEditHealthMutation,
	useDeleteAdmissionRequestMutation,
	useGetPendingSubmissionListQuery,
	useGetAwaitingListQuery,
	useGetPendingListQuery,
	useGetChangeHistoryListQuery,
	useGetChangeHistoryListDetailMutation,
	useGetMemberListQuery,
	useGetNextFileNoQuery,
	useRegisterProgramMutation,
	useRegisterCaseMutation,
	useOpenCaseMutation,
	useCloseCaseMutation,
	useGetFamilyMemberGroupListMutation,
	useCloseAllProgramMutation,
	useCloseAllCaseMutation,
	useSubmitToICMutation,
	useReturnFromICMutation,
	useAssignCaseWorkersMutation,
	useAssignProgramWorkersMutation,
	useCloseNotSuitableMutation,
	useTransferCaseWorkersMutation,
	useUpdateShareProfessionalsMutation,
	useTransferProgramWorkersMutation,
	useClearAllCaseWorkersMutation,

	useClearAllProgramWorkersMutation,
	useGetNextFileNoStringMutation,
} = admissionRequest;
